import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getGroupsState } from '../../../features/groups/selector';
import { getAllProjectUsersState } from '../../../features/project/selectors';
import { Typography } from '@material-ui/core';
import { ICompany, IUser } from '../../../api-client/autogenerated';
import { getFilteredUsersFromUserGroup } from '../../../scripts/utils';
import { getCompaniesState } from '../../../features/companies/selector';
import { ManagePermissionsDialogType } from '../../design/ManagePermissionsDialog';
import _ from 'lodash';

type Props = {
  associatedUsers: string[];
  associatedGroups: string[];
};

interface CompanyWithUsers {
  company?: ICompany;
  users: IUser[];
}

export default function InlineNotificationList(props: Props) {
  const { associatedUsers, associatedGroups } = props;

  const groups = useSelector(getGroupsState);
  const companies = useSelector(getCompaniesState);
  const projectUsers = useSelector(getAllProjectUsersState);

  const notifiedUsers = useMemo<CompanyWithUsers[]>(() => {
    const groupUsers = associatedGroups.flatMap((groupId) => {
      const group = groups.find((g) => g.id === groupId);
      return getFilteredUsersFromUserGroup(ManagePermissionsDialogType.AssociatedUsers, group);
    });

    const directUsers = associatedUsers.flatMap((userId) => {
      const user = projectUsers.find((pUser) => pUser.userId === userId)?.user;
      return user ? [user] : [];
    });

    const byCompany = new Map<string | undefined, CompanyWithUsers>();
    [...groupUsers, ...directUsers].forEach((user) => {
      const companyId = user.companyId ?? undefined;
      if (byCompany.has(companyId)) {
        byCompany.get(companyId)!.users.push(user);
      } else {
        byCompany.set(companyId, {
          company: companies.find((c) => c.id === companyId),
          users: [user],
        });
      }
    });

    return Array.from(byCompany.values());
  }, [associatedUsers, associatedGroups, groups, companies, projectUsers]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {notifiedUsers.map(({ company, users }, index) => (
        <div
          key={company?.id || index}
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            marginBottom: '4px',
          }}
        >
          <Typography style={{ fontSize: 16, fontWeight: 700 }}>
            {company?.name || 'Unknown Company'}
          </Typography>
          {_.uniqBy(users, 'id').map((user) => (
            <Typography key={user.id} style={{ fontSize: 12 }}>
              {user.name}
            </Typography>
          ))}
        </div>
      ))}
    </div>
  );
}
